<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useLocaleStore } from '@/stores/locale'
import { applyDocumentTitle } from '@/utils/documentTitle'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import ConfirmDialogHost from '@/components/common/ConfirmDialogHost.vue'
import SessionExpiredModal from '@/components/common/SessionExpiredModal.vue'
import PlanLimitModal from '@/components/common/PlanLimitModal.vue'
import PwaUpdateBanner from '@/components/pwa/PwaUpdateBanner.vue'
import PwaInstallBanner from '@/components/pwa/PwaInstallBanner.vue'
import FeatureUnlockToast from '@/components/milestones/FeatureUnlockToast.vue'
import { useSessionExpired } from '@/composables/useSessionExpired'
import { useMilestoneUnlock } from '@/composables/useMilestoneUnlock'
import { setupForegroundPushListener } from '@/composables/usePushNotifications'
import { useAuthStore } from '@/stores/auth'
import { useLabelOverridesStore } from '@/stores/labelOverrides'
import { usePaymentMethodsStore } from '@/stores/paymentMethods'
import { useStatusRegistryStore } from '@/stores/statusRegistry'
import { performLogout } from '@/utils/logout-cleanup'

const { sessionExpiredOpen, sessionExpiredCanRefresh } = useSessionExpired()
const { initMilestoneUnlock } = useMilestoneUnlock()
const authStore = useAuthStore()
const labelOverridesStore = useLabelOverridesStore()
const paymentMethodsStore = usePaymentMethodsStore()
const statusRegistryStore = useStatusRegistryStore()
const localeStore = useLocaleStore()
const route = useRoute()

watch(
  () => localeStore.currentLocale,
  () => applyDocumentTitle(route),
)

onMounted(() => {
  applyDocumentTitle(route)
  initMilestoneUnlock()
  attachPushListenerIfLoggedIn()
})
function attachPushListenerIfLoggedIn() {
  if (authStore.isAuthenticated) {
    void setupForegroundPushListener()
  }
}

watch(() => authStore.isAuthenticated, attachPushListenerIfLoggedIn)

watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void labelOverridesStore.load()
      void paymentMethodsStore.load()
      void statusRegistryStore.ensureLoaded()
    } else {
      labelOverridesStore.reset()
      paymentMethodsStore.reset()
      statusRegistryStore.reset()
    }
  },
)

const handleExit = () => {
  void performLogout()
}

const handleStay = () => {
  // choose('stay') in modal resolves api interceptor; refresh + reload done in api.ts
}
</script>

<template>
  <ErrorBoundary>
    <SessionExpiredModal
      :open="sessionExpiredOpen"
      :can-refresh="sessionExpiredCanRefresh"
      @exit="handleExit"
      @stay="handleStay"
    />
    <PlanLimitModal />
    <ConfirmDialogHost />
    <PwaUpdateBanner />
    <PwaInstallBanner />
    <FeatureUnlockToast />
    <RouterView />
  </ErrorBoundary>
</template>
